/**
 * CaseMaker Service Layer
 *
 * Handles all API calls to the CaseMaker Cloud Functions.
 * Uses HTTP endpoints with Bearer token authentication.
 */

import { auth } from '../lib/firebase';
import { buildHttpHeaders } from '../utils/appCheckToken';
import { getCloudFunctionsBaseUrl } from '../utils/cloudFunctionUrl';
import type {
  GenerateDocRequest,
  GenerateDocResponse,
  UpdateGeneratedDocRequest,
  UpdateGeneratedDocResponse,
  ListGeneratedDocsRequest,
  ListGeneratedDocsResponse,
  GetGeneratedDocRequest,
  GetGeneratedDocResponse,
  RegenerateDocRequest,
  RegenerateDocResponse,
  ExportGeneratedDocRequest,
  ExportGeneratedDocResponse,
  DeleteGeneratedDocRequest,
  DeleteGeneratedDocResponse,
  ServiceHttpError,
} from '../types/generatedDoc';

/**
 * Get the current user's ID token for authentication
 */
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.getIdToken();
}

/**
 * Make an authenticated request to a CaseMaker endpoint
 */
async function makeRequest<TRequest, TResponse>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  data?: TRequest
): Promise<TResponse> {
  const token = await getAuthToken();
  const baseUrl = getCloudFunctionsBaseUrl();
  const url = `${baseUrl}/${endpoint}`;
  const headers = await buildHttpHeaders(token);

  const options: RequestInit = {
    method,
    headers,
  };

  if (data && (method === 'POST' || method === 'PUT')) {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(url, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

    // Handle rate limiting (429 Too Many Requests)
    if (response.status === 429) {
      const retryAfter = errorData.retryAfterSeconds || 'a few';
      throw new Error(`You're doing a lot! Please wait ${retryAfter} seconds and try again.`);
    }

    // IMPL-002: attach the HTTP status and parsed body so callers can route
    // on structured gate codes (ACK_REQUIRED, ELEVATED_ACK_REQUIRED,
    // AUDIT_UNAVAILABLE, DOC_NOT_EXPORTABLE) instead of message text. The
    // message itself is unchanged, so existing callers are unaffected.
    const err: ServiceHttpError = new Error(
      errorData.error || `Request failed with status ${response.status}`
    );
    err.status = response.status;
    err.body = errorData;
    throw err;
  }

  return response.json() as Promise<TResponse>;
}

/**
 * CaseMaker Service
 *
 * Provides methods for all CaseMaker document operations.
 */
export const casemakerService = {
  /**
   * Generate a new advocacy document
   */
  async generateDocument(request: GenerateDocRequest): Promise<GenerateDocResponse> {
    return makeRequest<GenerateDocRequest, GenerateDocResponse>(
      'generateAdvocacyDocHttp',
      'POST',
      request
    );
  },

  /**
   * Update an existing generated document
   */
  async updateDocument(request: UpdateGeneratedDocRequest): Promise<UpdateGeneratedDocResponse> {
    return makeRequest<UpdateGeneratedDocRequest, UpdateGeneratedDocResponse>(
      'updateGeneratedDocHttp',
      'POST',
      request
    );
  },

  /**
   * List generated documents with optional filtering
   */
  async listDocuments(request: ListGeneratedDocsRequest = {}): Promise<ListGeneratedDocsResponse> {
    return makeRequest<ListGeneratedDocsRequest, ListGeneratedDocsResponse>(
      'listGeneratedDocsHttp',
      'POST',
      request
    );
  },

  /**
   * Get a single generated document by ID
   */
  async getDocument(request: GetGeneratedDocRequest): Promise<GetGeneratedDocResponse> {
    return makeRequest<GetGeneratedDocRequest, GetGeneratedDocResponse>(
      'getGeneratedDocHttp',
      'POST',
      request
    );
  },

  /**
   * Regenerate a document with updated context
   */
  async regenerateDocument(request: RegenerateDocRequest): Promise<RegenerateDocResponse> {
    return makeRequest<RegenerateDocRequest, RegenerateDocResponse>(
      'regenerateDocHttp',
      'POST',
      request
    );
  },

  /**
   * Export a document to PDF or DOCX format
   */
  async exportDocument(request: ExportGeneratedDocRequest): Promise<ExportGeneratedDocResponse> {
    return makeRequest<ExportGeneratedDocRequest, ExportGeneratedDocResponse>(
      'exportGeneratedDocHttp',
      'POST',
      request
    );
  },

  /**
   * Delete a generated document
   */
  async deleteDocument(request: DeleteGeneratedDocRequest): Promise<DeleteGeneratedDocResponse> {
    return makeRequest<DeleteGeneratedDocRequest, DeleteGeneratedDocResponse>(
      'deleteGeneratedDocHttp',
      'POST',
      request
    );
  },
};

export default casemakerService;
